import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Badge, INTENT, flattenEtapes, phaseByEtape } from "../../lib/helpers";
import * as Icons from "lucide-react";
import { ICONS_BY_NAME } from "../../lib/icons";

const CATS = {
  a_venir: { label: "À venir", icon: "CalendarClock", color: INTENT.inerte.color },
  en_cours: { label: "En cours", icon: "Loader", color: INTENT.en_cours.color },
  passe: { label: "Passés", icon: "CheckCircle2", color: INTENT.acquis.color },
};
const projectCat = (p) => (p.progress >= 100 ? "passe" : p.progress > 0 ? "en_cours" : "a_venir");

const ProjectRow = ({ p, onOpen }) => {
  // Lot 4d : ne remonte que les étapes d'une phase en_cours — même règle que le
  // grisé (Lot 4b) et que client_dashboard (Journal Horizon). Sans ce filtre, une
  // étape non obligatoire laissée active dans une phase cloturee (trace délibérée,
  // Lot 3b) pourrait afficher un badge « Action requise » sur un projet dont la
  // phase active réelle n'attend rien.
  const phaseMap = phaseByEtape(p);
  const activeEtapes = flattenEtapes(p).filter((e) => phaseMap[e.key]?.status === "en_cours");
  const awaiting = activeEtapes.find((x) => x.status === "en_attente_client");
  const current = awaiting
    || activeEtapes.find((x) => x.status === "en_correction")
    || activeEtapes.find((x) => x.status === "en_attente_agence")
    || activeEtapes.find((x) => x.status === "en_cours");
  return (
    <button onClick={onOpen} data-testid={`project-${p.id}`}
      className={`text-left bg-sable rounded-sm border shadow-sm p-6 card-lift ${awaiting ? "border-ambre/50 ring-1 ring-ambre/20 hover:border-ambre" : "border-marine/10 hover:border-marine/40"}`}>
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex items-center gap-2.5 flex-wrap">
          <h3 className="font-display text-lg text-marine">{p.name}</h3>
          {awaiting && <span data-testid={`project-awaiting-${p.id}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[9px] font-medium ${INTENT.attente.cls}`}>Action requise : validation</span>}
        </div>
        <Icons.ChevronRight size={16} className="text-marine shrink-0" />
      </div>
      <div className="flex items-center gap-4">
        <div className="flex-1 h-2 bg-sable border border-marine/30 rounded-full overflow-hidden"><div className="h-full bg-marine" style={{ width: `${p.progress}%` }} /></div>
        <span className="text-sm font-medium text-marine">{p.progress}%</span>
      </div>
      {current && <div className="mt-3 flex items-center gap-2 text-sm text-marine/80">Étape actuelle : <Badge status={current.status} /> <span>{current.name}</span></div>}
    </button>
  );
};

export default function ClientProjects() {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("en_cours");
  const nav = useNavigate();
  useEffect(() => { api.get("/client/projects").then((r) => setProjects(r.data)); }, []);

  const groups = projects.reduce((acc, p) => {
    const g = acc[p.type] = acc[p.type] || { type: p.type, label: p.type_label, description: p.type_description || "", list: [] };
    g.list.push(p);
    return acc;
  }, {});
  const groupList = Object.values(groups);

  // ---- Module detail view ----
  if (selected && groups[selected]) {
    const g = groups[selected];
    const buckets = { a_venir: [], en_cours: [], passe: [] };
    g.list.forEach((p) => buckets[projectCat(p)].push(p));
    return (
      <div>
        <button onClick={() => setSelected(null)} data-testid="back-to-modules" className="text-sm text-marine/80 hover:text-marine flex items-center gap-1 mb-3"><Icons.ArrowLeft size={16} className="text-marine" /> Tous les modules</button>
        <div className="mb-1">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-bordeaux">Module</div>
          <h1 className="font-display text-2xl text-marine">{g.label}</h1>
        </div>
        {g.description && <p className="text-sm text-marine/80 mb-5 max-w-3xl leading-relaxed" data-testid={`module-desc-${g.type}`}>{g.description}</p>}

        <div className="flex flex-wrap gap-2 mb-6" data-testid="client-project-tabs">
          {Object.entries(CATS).map(([k, meta]) => {
            const TabIc = ICONS_BY_NAME[meta.icon] || Icons.Circle;
            const active = tab === k;
            return (
              <button key={k} onClick={() => setTab(k)} data-testid={`client-project-tab-${k}`}
                className={`px-4 py-1.5 rounded-sm text-sm border flex items-center gap-1.5 transition-colors ${active ? "bg-marine text-white border-marine" : "border-marine/20 text-marine hover:bg-marine/5"}`}>
                <TabIc size={16} style={active ? {} : { color: meta.color }} /> {meta.label}
                <span className={`text-xs px-1.5 rounded-sm ${active ? "bg-white/20" : "bg-sable border border-marine/30 text-marine"}`}>{buckets[k].length}</span>
              </button>
            );
          })}
        </div>

        <div className="grid gap-4" data-testid="client-project-list">
          {buckets[tab].length === 0 && <p className="text-sm text-marine/80">Aucun projet dans cette catégorie.</p>}
          {buckets[tab].map((p) => <ProjectRow key={p.id} p={p} onOpen={() => nav(`/projets/${p.id}`)} />)}
        </div>
      </div>
    );
  }

  // ---- Module grid view ----
  return (
    <div>
      <PageTitle overline="Suivi" title="Mes Projets" />
      {groupList.length === 0 && <p className="text-sm text-marine/80">Aucun projet pour le moment.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="client-modules-grid">
        {groupList.map((g) => {
          return (
            <button key={g.type} onClick={() => { setSelected(g.type); setTab("en_cours"); }} data-testid={`client-module-${g.type}`}
              className="text-left p-6 rounded-sm border bg-sable border-marine/10 shadow-sm card-lift hover:border-marine/40 cursor-pointer">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider text-bordeaux font-semibold">Module</span>
                <span className="text-xs text-gris-texte bg-sable border border-marine/30 px-2 py-0.5 rounded-sm">{g.list.length} projet{g.list.length > 1 ? "s" : ""}</span>
              </div>
              <h2 className="font-display text-lg text-marine">{g.label}</h2>
              {g.description && <p className="text-sm text-marine/80 mt-1 line-clamp-2">{g.description}</p>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
